/**
 * SummaryCards component
 * Displays the top-level metrics for end-of-day summary:
 * - Total sessions (completed/total)
 * - Total boxes packed
 * - Total weight
 * - Average yield percentage
 * - Voided boxes
 * With dark mode support
 */
import React from 'react';
import { useLanguage } from '../../../../i18n/LanguageContext';
import { formatYield, YIELD_TARGET } from '../../../../hooks/useYieldFactor';
import { EndOfDaySummary } from '../../../../services/productionService';
import {
  ClipboardList,
  Package,
  Scale,
  BarChart3,
  Trash2,
} from 'lucide-react';

interface SummaryCardsProps {
  summary: EndOfDaySummary;
}

const SummaryCards: React.FC<SummaryCardsProps> = ({ summary }) => {
  const { t } = useLanguage();

  // Define card configurations with dark mode colors
  const cards = [
    {
      title: t('endOfDaySummary.totalSessions'),
      value: `${summary.completed_sessions}/${summary.total_sessions}`,
      subtitle: t('endOfDaySummary.completedSessions'),
      icon: ClipboardList,
      color: 'bg-blue-500',
      textColor: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      borderColor: 'border-blue-200 dark:border-blue-800',
    },
    {
      title: t('endOfDaySummary.totalBoxes'),
      value: summary.total_boxes.toLocaleString(),
      subtitle: `${summary.voided_boxes} ${t('endOfDaySummary.voided')}`,
      icon: Package,
      color: 'bg-green-500',
      textColor: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      borderColor: 'border-green-200 dark:border-green-800',
    },
    {
      title: t('endOfDaySummary.totalWeight'),
      value: `${summary.total_weight_lbs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lb`,
      subtitle: `${summary.total_waste_lbs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lb ${t('endOfDaySummary.waste')}`,
      icon: Scale,
      color: 'bg-purple-500',
      textColor: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      borderColor: 'border-purple-200 dark:border-purple-800',
    },
    {
      title: t('endOfDaySummary.totalWaste'),
      value: `${summary.total_waste_lbs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lb`,
      subtitle: summary.total_weight_lbs > 0
        ? `${((summary.total_waste_lbs / (summary.total_weight_lbs + summary.total_waste_lbs)) * 100).toFixed(2)}% ${t('endOfDaySummary.ofMaterial')}`
        : '0.00%',
      icon: Trash2,
      color: 'bg-red-500',
      textColor: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-red-50 dark:bg-red-900/20',
      borderColor: 'border-red-200 dark:border-red-800',
    },
    {
      title: t('endOfDaySummary.avgYield'),
      value: formatYield(summary.avg_yield_percentage),
      subtitle: summary.avg_yield_percentage >= YIELD_TARGET ? t('endOfDaySummary.onTarget') : t('endOfDaySummary.belowTarget'),
      icon: BarChart3,
      color: summary.avg_yield_percentage >= YIELD_TARGET ? 'bg-green-500' : 'bg-yellow-500',
      textColor: summary.avg_yield_percentage >= YIELD_TARGET ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400',
      bgColor: summary.avg_yield_percentage >= YIELD_TARGET ? 'bg-green-50 dark:bg-green-900/20' : 'bg-yellow-50 dark:bg-yellow-900/20',
      borderColor: summary.avg_yield_percentage >= YIELD_TARGET ? 'border-green-200 dark:border-green-800' : 'border-yellow-200 dark:border-yellow-800',
    },
  ];

  return (
    <div className="grid grid-cols-3 max-[425px]:grid-cols-2 lg:grid-cols-5 gap-4">
      {cards.map((card, index) => (
        <div
          key={index}
          className={`${card.bgColor} rounded-lg p-3 sm:p-4 shadow-sm border ${card.borderColor}`}
        >
          <div className="flex items-center justify-between">
            <div className={`${card.color} p-2 rounded-lg`}>
              <card.icon className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="mt-3">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{card.title}</p>
            <p className={`text-xl sm:text-2xl font-bold ${card.textColor} mt-1`}>{card.value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{card.subtitle}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SummaryCards;
